import React, {
  useRef, useEffect, useState, forwardRef,
} from 'react';

export interface DynamicVideoArgs {
  mp4Src?: string;
  webmSrc?: string;
  ignoreMissing?: boolean;
  poster?: string;
  className?: string;
  videoProps?: React.VideoHTMLAttributes<HTMLVideoElement>;
}

/**
 * Lazy-loads a single video element with dynamic source selection based on browser support.
 *
 * @param args - Configuration for the video element.
 * @returns The rendered video element.
 */
const DynamicVideo = forwardRef<HTMLVideoElement, DynamicVideoArgs>((args, ref) => {
  const internalRef = useRef<HTMLVideoElement | null>(null);
  const videoRef = (ref as React.RefObject<HTMLVideoElement>) || internalRef;
  const [videoConfig, setVideoConfig] = useState({ src: '', type: '' });

  useEffect(() => {
    const videoPlayer = videoRef.current;
    if (!videoPlayer) return;

    const tester = document.createElement('video');
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    let newConfig: { src: string; type: string } | null = null;

    if (isSafari && args.mp4Src && tester.canPlayType('video/mp4; codecs="hvc1"')) {
      newConfig = {
        src: args.mp4Src,
        type: 'video/mp4; codecs="hvc1"',
      };
    } else if (!isSafari && args.webmSrc && tester.canPlayType('video/webm; codecs="vp9"')) {
      newConfig = {
        src: args.webmSrc,
        type: 'video/webm',
      };
    } else if (args.webmSrc && args.ignoreMissing) {
      newConfig = {
        src: args.mp4Src,
        type: 'video/mp4',
      };
    } else if (args.mp4Src && args.ignoreMissing) {
      newConfig = {
        src: args.webmSrc,
        type: 'video/webm',
      };
    }

    if (newConfig) {
      setVideoConfig(newConfig);
    }
  }, [args.mp4Src, args.webmSrc, args.ignoreMissing]);

  useEffect(() => {
    const videoPlayer = videoRef.current;
    // If the video source is set, load and play the video to the first frame. Autoplay, if prop was passed.
    if (videoConfig.src && videoPlayer) {
      videoPlayer.load();
      videoPlayer.play()
        .then(() => {
          videoPlayer.pause();
          videoPlayer.currentTime = videoPlayer.duration;
        }).catch((error) => {
          console.info('Autoplay was prevented by the browser:', error);
        });

      const event = new CustomEvent('dynamicSourceSet', {
        bubbles: true,
        detail: { videoElement: videoPlayer, sourceUsed: videoConfig.src },
      });
      videoPlayer.dispatchEvent(event);
    }
  }, [videoConfig]);

  return (
    <video
      ref={videoRef}
      poster={args.poster}
      className={args.className}
      {...args.videoProps}
    >
      {videoConfig.src && <source src={videoConfig.src} type={videoConfig.type} />}
    </video>
  );
});

export default DynamicVideo;
